import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'

import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'

import { ScrollArea } from '@components/ui/scroll-area'
import IconArrowDown from '@images/icons/chevron-down.svg'
import IconCheckmark from '@images/icons/check.svg'
import IconSearch from '@images/icons/magnifier.svg'
import { cn } from '@lib/cn'

export interface Option extends Record<string, any> {
  label: React.ReactNode
  value: string
  disabled?: boolean
  options?: never
  className?: string
  keywords?: string[]
}

interface OptionGroup extends Record<string, any> {
  label: React.ReactNode
  options: Option[]
  value?: never
}

type Omitted = 'onChange' | 'defaultValue' | 'value' | 'prefix'

interface BaseComboboxProps extends Omit<React.ComponentPropsWithoutRef<'button'>, Omitted> {
  open?: boolean
  renderDropdownArrow?: boolean
  variant?: 'default' | 'unstyled'
  options: (Option | OptionGroup)[]
  allowSearch?: boolean
  placeholder?: string
  onSearch?: (search: string) => void
  onOpenChange?: (open: boolean) => void
  renderControlLabel?: (selectedOptions: Option[]) => React.ReactNode
  children?: React.ReactNode
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  menu?: React.ComponentProps<typeof Popover.Content>
  align?: 'start' | 'end' | 'center'
  borderless?: boolean
  searchPlaceholder?: string
  noOptionsLabel?: React.ReactNode
}

export interface SingleComboboxProps extends BaseComboboxProps {
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  multiple?: false
}

export interface MultiComboboxProps extends BaseComboboxProps {
  value?: string[]
  defaultValue?: string[]
  onChange?: (value: string[]) => void
  multiple: true
}

export type ComboboxProps = SingleComboboxProps | MultiComboboxProps

const EMPTY = '❌'

const textContent = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textContent).join('')
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textContent(node.props.children)
  return ''
}

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>((props, ref) => {
  const {
    options,
    value: _value,
    id,
    onChange,
    multiple,
    defaultValue,
    allowSearch,
    className,
    placeholder = 'Select an option',
    variant = 'default',
    onSearch,
    renderControlLabel,
    children: customControl,
    prefix,
    suffix,
    onOpenChange,
    menu: menuProps,
    align = 'start',
    open,
    renderDropdownArrow = true,
    borderless,
    searchPlaceholder = 'Search',
    noOptionsLabel = 'No options',
    ...rest
  } = props

  const isOpenControlled = open !== undefined
  const searchRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [_opened, setOpened] = useState(false)
  const opened = isOpenControlled ? open : _opened
  const [activeValue, setActiveValue] = useState<string>(EMPTY)
  const value = _value ?? defaultValue

  const flatOptions = useMemo(() => {
    return options.reduce((acc, option) => {
      if (option.options) return [...acc, ...option.options]
      return [...acc, option as Option]
    }, [] as Option[])
  }, [options])

  const selectedOptions = useMemo(() => {
    if (!multiple) {
      const option = flatOptions.find((option) => option.value === value)
      return option ? [option] : []
    }
    return flatOptions.filter((option) => (value as string[] | undefined)?.includes(option.value))
  }, [multiple, flatOptions, value])

  useEffect(() => {
    if (opened) return
    if (allowSearch || multiple) return setActiveValue(EMPTY)
    const selectedOption = flatOptions.find((option) => option.value === value)
    const label = textContent(selectedOption?.label)
    setActiveValue(label ? label + selectedOption?.value : EMPTY)
  }, [opened, multiple, allowSearch, flatOptions, value])

  const handleOpenChange = (next: boolean) => {
    if (onOpenChange) onOpenChange(next)
    else setOpened(next)
  }

  const handleSelect = (selectedValue: string) => {
    if (!multiple) {
      ;(onChange as SingleComboboxProps['onChange'])?.(selectedValue)
      handleOpenChange(false)
      return
    }

    const current = selectedOptions.map((option) => option.value)
    const next = current.includes(selectedValue)
      ? current.filter((item) => item !== selectedValue)
      : [...current, selectedValue]
    ;(onChange as MultiComboboxProps['onChange'])?.(next)
    searchRef.current?.focus()
  }

  const controlLabel = () => {
    return selectedOptions.map((option, index) => (
      <span key={option.value}>
        {option.label}
        {index !== selectedOptions.length - 1 && <span className="me-1">,</span>}
      </span>
    ))
  }

  const control = customControl ?? (
    <button
      ref={ref}
      type="button"
      data-state={!value || value === defaultValue ? 'idle' : 'selected'}
      id={id}
      className={cn(
        'group inline-flex items-center h-full text-sm leading-none text-o-ink focus:outline-hidden',
        {
          'gap-1 ps-[12px] transition ease-out duration-100 border rounded-lg min-h-[44px]': variant === 'default',
          'pe-[12px]': variant === 'default' && !suffix,
          'pe-[44px]': !!suffix,
          'border-o-line-strong hover:border-o-muted': variant === 'default' && !opened && !borderless,
          'border-o-muted': variant === 'default' && opened && !borderless,
          '!border-transparent hover:!border-o-line-strong': borderless && variant === 'default'
        },
        className
      )}
      {...rest}
    >
      {prefix}

      {selectedOptions.length === 0 ? (
        <span className="text-o-faint">{placeholder}</span>
      ) : (
        (renderControlLabel?.(selectedOptions) ?? (
          <span className="overflow-hidden w-full text-start truncate">{controlLabel()}</span>
        ))
      )}

      {renderDropdownArrow && (
        <span className="flex-shrink-0 ms-auto">
          <IconArrowDown
            className={cn(
              'fill-current text-o-muted size-4',
              variant === 'unstyled' && 'size-[14px] relative top-px'
            )}
          />
        </span>
      )}
    </button>
  )

  const trigger = suffix && !customControl ? (
    <div className="relative flex w-full h-full">
      <Popover.Trigger ref={triggerRef} asChild>
        {control}
      </Popover.Trigger>
      <div className="flex absolute inset-y-0 items-center end-[12px]">{suffix}</div>
    </div>
  ) : (
    <Popover.Trigger ref={triggerRef} asChild>
      {control}
    </Popover.Trigger>
  )

  return (
    <Popover.Root open={opened} onOpenChange={handleOpenChange}>
      {trigger}
      <Popover.Portal>
        <Popover.Content
          alignOffset={variant === 'unstyled' ? -18 : 0}
          align={align}
          style={{
            minWidth: 'var(--radix-popover-trigger-width)',
            maxHeight: 'min(calc(var(--radix-popover-content-available-height) - 40px), 445px)'
          }}
          {...menuProps}
          sideOffset={10}
          className={cn(
            'flex bg-o-card border border-o-line shadow-o-pane rounded-xl z-[999] focus:outline-hidden',
            menuProps?.className
          )}
        >
          <Command
            shouldFilter={allowSearch && !onSearch}
            className="flex w-full flex-col"
            value={activeValue}
            onValueChange={(next) => {
              if (activeValue !== EMPTY && next === EMPTY) return
              setActiveValue(next)
            }}
          >
            <div className={cn('relative', allowSearch ? 'px-[5px] mt-[5px]' : 'overflow-hidden h-0')}>
              <Command.Input
                className="bg-transparent outline-hidden border-none ring-0 focus:ring-0 text-sm text-o-ink placeholder:text-o-faint w-full ps-[7px] h-9"
                placeholder={searchPlaceholder}
                ref={searchRef}
                onValueChange={onSearch}
              />
              <span className="absolute top-1/2 -translate-y-1/2 end-[14px]" aria-hidden="true">
                <IconSearch className="size-4 text-o-muted fill-current" />
              </span>
            </div>
            <ScrollArea id="scroll-area">
              <Command.List className="p-[5px]">
                <Command.Empty className="flex justify-center items-center h-10 text-sm text-center opacity-50">
                  {noOptionsLabel}
                </Command.Empty>
                {options.length === 0 && (
                  <div className="flex justify-center items-center h-10 text-sm text-center opacity-50">
                    {noOptionsLabel}
                  </div>
                )}
                <Command.Item value={EMPTY} className="hidden">
                  {EMPTY}
                </Command.Item>
                {options.map((option, groupIndex) => {
                  const items = (option.options ?? [option as Option]).map((item) => {
                    const isSelected = !!selectedOptions.find((selected) => selected.value === item.value)

                    return (
                      <Command.Item
                        key={item.value}
                        value={textContent(item.label) + item.value}
                        keywords={item.keywords}
                        onSelect={() => handleSelect(item.value)}
                        disabled={item.disabled}
                        data-active={isSelected ? 'true' : 'false'}
                        className={cn(
                          'group relative mb-1 last:mb-0 text-sm leading-none focus:outline-hidden text-o-ink rounded-[7px] flex items-center min-h-[40px] ps-[7px] pe-[35px] select-none cursor-pointer',
                          'aria-selected:bg-o-chip aria-selected:text-o-ink',
                          'aria-disabled:opacity-50',
                          isSelected && !multiple && 'border border-o-line',
                          item.className
                        )}
                      >
                        {item.label}
                        <span
                          className={cn(
                            'absolute top-1/2 -translate-y-1/2 end-[10px]',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        >
                          <IconCheckmark className="size-[18px] fill-current text-o-slate" />
                        </span>
                      </Command.Item>
                    )
                  })

                  if (option.options) {
                    return (
                      <Command.Group
                        key={`group-${groupIndex}`}
                        heading={
                          <div className="flex items-center h-9 px-2.5">
                            <span className="text-xs font-medium tracking-wider text-o-muted uppercase">
                              {option.label}
                            </span>
                          </div>
                        }
                      >
                        {items}
                      </Command.Group>
                    )
                  }

                  return items[0]
                })}
              </Command.List>
            </ScrollArea>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
})

Combobox.displayName = 'Combobox'
